import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { selectUser } from 'src/app/auth/reducers/auth.reducer';
import { Manual } from 'src/app/core/models/Manual';
import { ItineraryService } from 'src/app/search-result/services/itinerary.service';
import * as LayoutActions from '../actions/layout.actions';
import * as fromReducers from '../reducers/layout.reducer';
import {
  BreadcrumbRoute,
  User,
  ContractError,
  ChangePasswordRequest,
  IAlertMessage
} from 'src/app/core/models/models';
import {
  LayoutState,
  selectBredcrumb,
  selectError,
  selectOpenChangePassword,
  selectManualInfo,
  selectManualContent,
  selectAlertMessages,
  selectLanguage,
  selectBookingCredentialsState
} from '../reducers/layout.reducer';
import {
  IBookingCredentialsData,
  IBookingCredentialsState
} from 'src/app/core/models/booking-credentials.model';

@Injectable({
  providedIn: 'root'
})
export class LayoutService {
  breadcrumb$: Observable<BreadcrumbRoute[]>;
  exceptionMode$: Observable<boolean>;
  showExceptionSwitch$: Observable<boolean>;
  search = {
    name: 'search',
    route: 'dashboard',
    isActive: false
  };

  getUser(): Observable<User> {
    return this.store.pipe(select(selectUser));
  }

  getGlobalError(): Observable<ContractError> {
    return this.store.pipe(select(selectError));
  }

  getManualInfo(): Observable<Manual> {
    return this.store.pipe(select(selectManualInfo));
  }

  getBookingCredentialsState(): Observable<IBookingCredentialsState> {
    return this.store.pipe(select(selectBookingCredentialsState));
  }

  getManualContent(): Observable<string> {
    return this.store.pipe(select(selectManualContent));
  }

  closeGenericError() {
    this.store.dispatch(LayoutActions.CloseError());
  }

  getActionActive(): Observable<string> {
    return this.service.getActionActive();
  }

  getLoadingActions(): Observable<boolean> {
    return this.service.getLoadingActions();
  }

  getShowExceptionSwitch(): Observable<boolean> {
    return this.service.getShowExceptionSwitch();
  }

  getShowException(): Observable<boolean> {
    return this.service.getExceptionMode();
  }

  getBreadcrumb(): Observable<BreadcrumbRoute[]> {
    return this.store.pipe(select(selectBredcrumb));
  }

  getAlertMessages(): Observable<IAlertMessage[]> {
    return this.store.pipe(select(selectAlertMessages));
  }

  saveNewAlertMessage(alertMessage: IAlertMessage) {
    this.store.dispatch(LayoutActions.SaveNewAlertMessage({ alertMessage }));
  }

  requireBookingCredentials() {
    this.store.dispatch(LayoutActions.RequireBookingCredentials());
  }

  updateBookingCredentials(data: IBookingCredentialsData) {
    this.store.dispatch(LayoutActions.UpdateBookingCredentials({ credentials: data }));
  }

  saveLanguage(language: string) {
    this.store.dispatch(LayoutActions.SaveLanguage({ language }));
  }

  removeAlertMessage(alertMessage: IAlertMessage) {
    this.store.dispatch(LayoutActions.RemoveAlertMessage({ alertMessage }));
  }

  showExceptionSwitch(showExceptionSwitch: boolean) {
    this.service.dispatchHideOrShowExceptionSwitch(showExceptionSwitch);
  }

  dispatchExceptionMode(exceptionMode: boolean) {
    this.service.dispatchExceptionMode(exceptionMode);
  }

  getOpenChangePassword(): Observable<boolean> {
    return this.store.pipe(select(selectOpenChangePassword));
  }

  dispatchOpenChangePassword(openChangePassword: boolean) {
    this.store.dispatch(LayoutActions.OpenChangePassword({ openChangePassword }));
  }

  dispatchChangePassword(request: ChangePasswordRequest) {
    this.store.dispatch(LayoutActions.ChangePassword({ request }));
  }

  dispatchManualInfo(language: string) {
    this.store.dispatch(LayoutActions.LoadManualInfo({ language }));
  }

  dispatchDownloadManual(id: string) {
    this.store.dispatch(LayoutActions.DownloadManual({ id }));
  }

  dispatchSetReceivedFrom(receivedFrom: string): void {
    this.store.dispatch(LayoutActions.SetReceivedFrom({ receivedFrom }));
  }

  dispatchRemoveReceivedFrom(): void {
    this.store.dispatch(LayoutActions.RemoveReceivedFrom());
  }

  dispatchChangeBreadcrumb(pnr: string, page: string) {
    this.store.dispatch(
      LayoutActions.ChangeBreadcrumb({
        breadcrumb: this.createBreadcrumb(pnr, page)
      })
    );
  }

  dispatchSabreAirlineEPRLogin(sabreAirlineCredentialsData: IBookingCredentialsData) {
    this.store.dispatch(
      LayoutActions.SabreAirlineEPRLogin({
        sabreAirlineCredentialsData
      })
    );
  }

  createBreadcrumb(pnr: string, page: string): BreadcrumbRoute[] {
    const breadcrumb = [this.search];
    const isActiveItinerary = page === 'itinerary' ? true : false;
    let itinerary: BreadcrumbRoute;

    this.service
      .getSearchForm()
      .pipe(take(1))
      .subscribe((val) => {
        const { bookingCode, gds, ticketNumbers } = val;
        itinerary = {
          name: `${pnr} - ${gds}`,
          route: 'itinerary',
          queryParams: { ...val },
          isActive: isActiveItinerary
        };

        breadcrumb.push(itinerary);

        if (page !== 'itinerary') {
          const route: BreadcrumbRoute = {
            name: page,
            route: page,
            queryParams: { ...val },
            isActive: true
          };
          breadcrumb.push(route);
        }
      });

    return breadcrumb;
  }

  getLanguage(): Observable<string> {
    return this.store.select(selectLanguage);
  }

  getReceivedFrom(): Observable<string> {
    return this.store.pipe(select(fromReducers.selectReceivedFrom));
  }

  constructor(private store: Store<LayoutState>, private service: ItineraryService) {
    this.exceptionMode$ = service.getExceptionMode();
  }
}
